import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Video, Play, RefreshCw, Loader2, Film, Download, ExternalLink, Mic } from 'lucide-react';
import { MusicBlock } from './VideoMusicBlock';

const API = process.env.REACT_APP_BACKEND_URL;

const SCENES = [
  { id: 'intro', name: 'Matrix Intro', description: 'Figure holding BETHCOIN check in Matrix rain' },
  { id: 'subscription', name: 'Treasury Officer', description: '$499/month subscription reveal' },
  { id: 'process_check', name: 'Process a Check', description: 'Creating and sending a Treasury Check' },
  { id: 'payoff_debt', name: 'Pay Off Debt', description: 'Freedom from debt with BETHCOIN' },
  { id: 'full_promo', name: 'Full Promotional', description: 'Complete Matrix-style trailer' },
];

const NarrationBlock = ({ video, token, onStarted }) => {
  const [script, setScript] = useState('');
  const [busy, setBusy] = useState(false);
  const processing = video.narration_status === 'processing';
  const narratedSrc = video.narrated_url?.startsWith('/api') ? `${API}${video.narrated_url}` : video.narrated_url;

  const narrate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/video/${video.video_id}/narrate`,
        script.trim() ? { custom_script: script } : {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Adding dramatic voiceover... about a minute');
      onStarted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add voiceover');
    }
    setBusy(false);
  };

  return (
    <div className="mt-4 border-t border-slate-700 pt-4" data-testid={`narration-block-${video.video_id}`}>
      <div className="flex items-center gap-2 mb-2 text-emerald-400 text-sm font-medium">
        <Mic className="w-4 h-4" /> Dramatic Voiceover
      </div>
      {video.narration_status === 'completed' && video.narrated_url && (
        <div className="mb-3">
          <video controls className="w-full rounded-lg" data-testid={`narrated-video-${video.video_id}`}>
            <source src={narratedSrc} type="video/mp4" />
          </video>
          <a href={narratedSrc} download data-testid={`download-narrated-${video.video_id}`}
            className="mt-2 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm flex items-center justify-center gap-2 transition-colors">
            <Download className="w-4 h-4" /> Download narrated video
          </a>
        </div>
      )}
      {video.narration_status === 'failed' && (
        <p className="text-sm text-red-400 mb-2" data-testid={`narration-error-${video.video_id}`}>{video.narration_error}</p>
      )}
      <textarea value={script} onChange={(e) => setScript(e.target.value)} maxLength={4000}
        placeholder="Optional: your own narration script. Leave blank for the built-in dramatic BETH script."
        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 h-20 resize-none text-sm"
        data-testid={`narration-script-${video.video_id}`} />
      <button onClick={narrate} disabled={busy || processing} data-testid={`narrate-btn-${video.video_id}`}
        className="mt-2 w-full py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white rounded-lg text-sm flex items-center justify-center gap-2 transition-colors">
        {processing || busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding voiceover...</>
          : <><Mic className="w-4 h-4" /> {video.narrated_url ? 'Re-record voiceover' : 'Add dramatic voiceover'}</>}
      </button>
    </div>
  );
};

export const VideoGeneratorPanel = ({ token }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedScene, setSelectedScene] = useState('full_promo');
  const [customPrompt, setCustomPrompt] = useState('');
  const [duration, setDuration] = useState('10');
  const [pollingId, setPollingId] = useState(null);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/video/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(res.data.videos || []);
    } catch (e) {
      toast.error('Failed to load videos');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchVideos();
  }, [token]);

  const narrating = videos.some(v => v.narration_status === 'processing' || v.music_status === 'processing');
  useEffect(() => {
    if (!narrating) return;
    const t = setInterval(fetchVideos, 8000);
    return () => clearInterval(t);
  }, [narrating]);

  // Poll for generating videos
  useEffect(() => {
    if (!pollingId) return;
    
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/video/status/${pollingId}`);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setPollingId(null);
          setGenerating(false);
          fetchVideos();
          if (res.data.status === 'completed') {
            toast.success('Video generated successfully!');
          } else {
            toast.error(`Video generation failed: ${res.data.error}`);
          }
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [pollingId]);

  const generateVideo = async () => {
    setGenerating(true);
    try {
      const payload = {
        scene: selectedScene,
        duration,
        aspect_ratio: '16:9'
      };
      if (customPrompt.trim()) {
        payload.custom_prompt = customPrompt;
      }
      
      const res = await axios.post(`${API}/api/video/generate`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Video generation started! This may take 5-10 minutes.');
      setPollingId(res.data.video_id);
      fetchVideos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start video generation');
      setGenerating(false);
    }
  };

  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="video-generator-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Film className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-bold text-white">Matrix Video Generator</h2>
        </div>
        <button 
          onClick={fetchVideos}
          disabled={loading}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Generate New Video */}
      <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700 mb-6">
        <h3 className="text-white font-medium mb-4">Generate New Matrix Video</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm text-slate-400 block mb-2">Scene Type</label>
            <select
              value={selectedScene}
              onChange={(e) => setSelectedScene(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              data-testid="video-scene-select"
            >
              {SCENES.map(scene => (
                <option key={scene.id} value={scene.id}>{scene.name}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {SCENES.find(s => s.id === selectedScene)?.description}
            </p>
          </div>
          
          <div>
            <label className="text-sm text-slate-400 block mb-2">Duration</label>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
            >
              <option value="5">5 seconds</option>
              <option value="10">10 seconds</option>
            </select>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="text-sm text-slate-400 block mb-2">Custom Prompt (optional)</label>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Override with your own Matrix-themed prompt..."
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 h-24 resize-none"
            data-testid="video-custom-prompt"
          />
        </div>
        
        <button
          onClick={generateVideo}
          disabled={generating}
          className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          data-testid="generate-video-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating (5-10 min)...
            </>
          ) : (
            <>
              <Video className="w-4 h-4" />
              Generate Matrix Video
            </>
          )}
        </button>
      </div>

      {/* Video List */}
      <div>
        <h3 className="text-white font-medium mb-4">Generated Videos</h3>
        {videos.length === 0 ? (
          <p className="text-slate-400 text-center py-8">No videos generated yet</p>
        ) : (
          <div className="space-y-4">
            {videos.map(video => (
              <div key={video.video_id} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-medium">{video.scene || 'Custom'}</p>
                    <p className="text-xs text-slate-500">{video.video_id}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(video.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    video.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                    video.status === 'generating' ? 'bg-amber-500/20 text-amber-400' :
                    video.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                    'bg-slate-500/20 text-slate-400'
                  }`}>
                    {video.status}
                  </span>
                </div>
                
                {video.status === 'completed' && video.video_url && (
                  <div className="mt-4">
                    <video 
                      controls 
                      className="w-full rounded-lg"
                      poster="/banner-strip.png"
                    >
                      <source src={video.video_url} type="video/mp4" />
                    </video>
                    <div className="flex gap-2 mt-2">
                      <a
                        href={video.video_url}
                        download
                        className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 transition-colors"
                      >
                        <Download className="w-4 h-4" /> Download
                      </a>
                      <a
                        href={video.video_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" /> Open
                      </a>
                    </div>
                  </div>
                )}
                
                {video.status === 'completed' && video.local_path && (
                  <NarrationBlock video={video} token={token} onStarted={fetchVideos} />
                )}
                {video.narration_status === 'completed' && (
                  <MusicBlock video={video} token={token} onStarted={fetchVideos} />
                )}

                {video.status === 'generating' && (
                  <div className="mt-4 flex items-center gap-2 text-amber-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Generating... (5-10 minutes)</span>
                  </div>
                )}
                
                {video.status === 'failed' && video.error && (
                  <p className="mt-2 text-sm text-red-400">{video.error}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoGeneratorPanel;
